import React, { FC, useMemo } from 'react'

import { StandardButton, Tooltip } from 'src/components/atoms'

import { useAuth } from 'src/hooks'
import { ProjectEntity } from 'src/types'

type Props = {
  project: Pick<ProjectEntity, 'adminUrl' | 'presentationUrl' | 'status' | 'isDeleting'>
  settingButtonText?: string
  presentationButtonText?: string
  iconPosition: 'left' | 'right'
  size?: 's' | 'm' | 'l'
  className?: string
}

export const ProjectListTableRowLinkButtons: FC<Props> = ({
  project,
  settingButtonText = '設定',
  presentationButtonText = 'プレゼン',
  iconPosition,
  size,
  className,
}) => {
  const { loadAccessToken } = useAuth()

  const handleOnClickCompassSettingPage = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const accessToken = loadAccessToken()
    window.open(`${project.adminUrl}/?works_token=${accessToken}`, '_blank')
  }

  const handleOnClickCompassPresentationPage = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const accessToken = loadAccessToken()
    window.open(`${project.presentationUrl}/?works_token=${accessToken}&referrer=works`, '_blank')
  }

  const isProjectStopped = useMemo(() => project.status.name === 'stopped', [project])
  const isProjectArchived = useMemo(() => project.status.name === 'archived', [project])

  // 設定ボタンの無効化判定。
  // プロジェクトが停止中またはアーカイブされている場合、または削除中の場合は設定ボタンを無効にする
  const isSettingButtonDisabled = useMemo(() => {
    return isProjectStopped || isProjectArchived || project.isDeleting
  }, [isProjectArchived, isProjectStopped, project.isDeleting])

  // 設定ボタンのツールチップの無効化判定。
  // プロジェクト削除中は無効化。またプロジェクトが停止中またはアーカイブされている場合以外はツールチップを無効化
  const isSettingTooltipDisabled = useMemo(() => {
    if (project.isDeleting) {
      return true
    }
    return !(isProjectStopped || isProjectArchived)
  }, [isProjectArchived, isProjectStopped, project.isDeleting])

  // 設定ボタンのツールチップのテキスト
  const settingTooltipText = useMemo(() => {
    if (isProjectStopped) {
      return '「停止中」です。\nアクセスにはステータス変更が必要です。'
    }
    if (isProjectArchived) {
      return 'アクセスするには準備中または稼働中に\nステータスを変更が必要です。'
    }
  }, [isProjectArchived, isProjectStopped])

  // プレゼンボタンの無効化判定。
  // プロジェクトが停止中または削除中の場合はプレゼンボタンを無効にする
  const isPresentationButtonDisabled = useMemo(() => {
    return isProjectStopped || project.isDeleting
  }, [isProjectStopped, project.isDeleting])

  // プレゼンボタンのツールチップの無効化判定。
  // プロジェクト削除中は無効化。プロジェクトが停止中の場合以外はツールチップを無効化
  const isPresentationTooltipDisabled = useMemo(() => {
    if (project.isDeleting) {
      return true
    }
    return !isProjectStopped
  }, [isProjectStopped, project.isDeleting])

  return (
    <div className={className}>
      <div className="tw-flex tw-gap-s-2">
        <Tooltip
          disabled={isSettingTooltipDisabled}
          showIcon={false}
          placement="top"
          text={settingTooltipText}
        >
          <StandardButton
            size={size}
            buttonType="secondary"
            text={settingButtonText}
            disabled={isSettingButtonDisabled}
            prependIconPath={iconPosition === 'left' ? 'external-link/soft/line' : undefined}
            appendIconPath={iconPosition === 'right' ? 'external-link/soft/line' : undefined}
            onClick={isProjectStopped ? undefined : handleOnClickCompassSettingPage}
          />
        </Tooltip>
        <Tooltip
          disabled={isPresentationTooltipDisabled}
          showIcon={false}
          placement="top"
          text={`「停止中」です。\nアクセスにはステータス変更が必要です。`}
        >
          <StandardButton
            size={size}
            buttonType="secondary"
            text={presentationButtonText}
            disabled={isPresentationButtonDisabled}
            prependIconPath={iconPosition === 'left' ? 'external-link/soft/line' : undefined}
            appendIconPath={iconPosition === 'right' ? 'external-link/soft/line' : undefined}
            onClick={isProjectStopped ? undefined : handleOnClickCompassPresentationPage}
          />
        </Tooltip>
      </div>
    </div>
  )
}
